import { useMemo } from 'react';
import {
    BudgetCategoryGroupFieldsFragment,
    BudgetPurpose,
} from '../../graphql/generated';
import { roundCentsToNearestDollar } from '../../util/currency';
import { typedKeys } from '../../util/objects';
import { CashflowTransactionEntry } from '../cashflow';
import { isSpendingBudgetPurpose } from './budgetPurpose';
import { buildBudgetCategoryGroupMap } from './buildBudgetCategoryGroupMap';

export const useActualAmountsByBudgetPurpose = ({
    budgetCategoryGroups,
    transactionEntries,
}: {
    budgetCategoryGroups: BudgetCategoryGroupFieldsFragment[];
    transactionEntries: CashflowTransactionEntry[];
}): Record<BudgetPurpose, number> =>
    useMemo(() => {
        const budgetCategoryGroupMap =
            buildBudgetCategoryGroupMap(budgetCategoryGroups);

        const actualAmounts = transactionEntries.reduce<
            Record<BudgetPurpose, number>
        >(
            (acc, t) => {
                const budgetCategoryGroup =
                    budgetCategoryGroupMap[t.category.categoryGroup.id];

                // If there's a budget category group, use its purpose
                // Otherwise fall back to the default purpose
                const budgetPurpose = budgetCategoryGroup
                    ? budgetCategoryGroup.budgetPurpose
                    : t.category.categoryGroup.defaultBudgetPurpose;

                if (budgetPurpose) {
                    acc[budgetPurpose] += isSpendingBudgetPurpose(budgetPurpose)
                        ? -t.amount
                        : t.amount;
                }

                return acc;
            },
            {
                fixed: 0,
                lifestyle: 0,
                saving_investing: 0,
                income: 0,
            }
        );

        typedKeys(actualAmounts).forEach((key) => {
            actualAmounts[key] = roundCentsToNearestDollar(actualAmounts[key]);
        });

        return actualAmounts;
    }, [budgetCategoryGroups, transactionEntries]);
